import { useEffect, useMemo, useRef, useState } from "react"
import { X } from "lucide-react"
import type { PageFeedbackAnswer } from "@/lib/api"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PAGE_DISPLAY_NAMES, PAGE_QUESTIONS, type PageSlug, type Segment } from "./questions"

interface FeedbackPanelProps {
  pageSlug: PageSlug
  onScoreSelect?: (score: number) => void
  onSubmit: (score: number, answers: PageFeedbackAnswer[]) => void
  onClose: () => void
  isSubmitting?: boolean
}

type PanelStep = "score" | "questions"

interface DraftAnswer {
  selectedOption: string
  otherText: string
}

function segmentFromScore(score: number): Segment {
  if (score >= 9) return "promoter"
  if (score >= 7) return "passive"
  return "detractor"
}

function scoreTone(score: number): string {
  if (score >= 9) return "bg-success/10 border-success/30 text-success"
  if (score >= 7) return "bg-warning/10 border-warning/30 text-warning"
  return "bg-destructive/10 border-destructive/30 text-destructive"
}

function scoreHoverTone(score: number): string {
  if (score >= 9) return "hover:bg-success/10 hover:text-success hover:border-success/20"
  if (score >= 7) return "hover:bg-warning/10 hover:text-warning hover:border-warning/20"
  return "hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20"
}

export function FeedbackPanel({
  pageSlug,
  onScoreSelect,
  onSubmit,
  onClose,
  isSubmitting = false,
}: FeedbackPanelProps) {
  const [step, setStep] = useState<PanelStep>("score")
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [draftAnswers, setDraftAnswers] = useState<Record<number, DraftAnswer>>({})
  const advanceTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current)
      }
    }
  }, [])

  const questions = useMemo(() => {
    if (!selectedSegment) return []
    const questionSet = PAGE_QUESTIONS[pageSlug]
    return [
      questionSet.segmentQuestion[selectedSegment],
      ...questionSet.commonQuestions,
    ]
  }, [pageSlug, selectedSegment])

  const currentQuestion = questions[questionIndex] || null
  const currentDraft = draftAnswers[questionIndex] || { selectedOption: "", otherText: "" }
  const selectedOther = currentDraft.selectedOption === "Other"
  const isLastQuestion = questionIndex === questions.length - 1
  const canSubmit = Boolean(currentDraft.selectedOption)
  const showContinueButton = selectedOther && !isLastQuestion
  const showSubmitButton = isLastQuestion && canSubmit

  function scheduleAdvance(nextIndex: number) {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current)
    }
    advanceTimerRef.current = window.setTimeout(() => {
      setQuestionIndex(nextIndex)
    }, 300)
  }

  function handleScoreClick(score: number) {
    const nextSegment = segmentFromScore(score)
    setSelectedScore(score)
    setSelectedSegment(nextSegment)
    setQuestionIndex(0)
    setDraftAnswers({})
    onScoreSelect?.(score)
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current)
    }
    advanceTimerRef.current = window.setTimeout(() => {
      setStep("questions")
    }, 300)
  }

  function handleOptionSelect(option: string) {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current)
    }
    setDraftAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        selectedOption: option,
        otherText: option === "Other" ? prev[questionIndex]?.otherText || "" : "",
      },
    }))

    if (option !== "Other" && !isLastQuestion) {
      scheduleAdvance(questionIndex + 1)
    }
  }

  function handleOtherTextChange(value: string) {
    setDraftAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        selectedOption: "Other",
        otherText: value,
      },
    }))
  }

  function buildAnswers(): PageFeedbackAnswer[] {
    return questions.map((question, index) => {
      const draft = draftAnswers[index]
      return {
        question: question.question,
        selected_option: draft?.selectedOption || "",
        other_text: draft?.selectedOption === "Other"
          ? draft.otherText.trim() || null
          : null,
      }
    }).filter((answer) => answer.selected_option)
  }

  function handleContinue() {
    if (!showContinueButton) return
    setQuestionIndex((current) => Math.min(current + 1, questions.length - 1))
  }

  function handleSubmit() {
    if (selectedScore == null) return
    onSubmit(selectedScore, buildAnswers())
  }

  return (
    <div className="relative w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-card shadow-xl">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close feedback"
        className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>

      <div className={cn("p-5 transition-opacity duration-200", step === "score" ? "opacity-100" : "opacity-0 pointer-events-none absolute inset-0")}>
        <div className="pr-8">
          <h3 className="text-sm font-semibold text-foreground">
            How useful is the {PAGE_DISPLAY_NAMES[pageSlug]} page?
          </h3>
        </div>

        <div className="mt-4 grid grid-cols-11 gap-1">
          {Array.from({ length: 11 }, (_, score) => {
            const isSelected = selectedScore === score
            return (
              <button
                key={score}
                type="button"
                onClick={() => handleScoreClick(score)}
                className={cn(
                  "aspect-square min-w-0 rounded-lg border text-sm font-medium transition-all duration-150",
                  isSelected
                    ? scoreTone(score)
                    : "border-transparent bg-muted/50 text-muted-foreground",
                  !isSelected && scoreHoverTone(score),
                )}
              >
                {score}
              </button>
            )
          })}
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Not useful</span>
          <span>Extremely useful</span>
        </div>
      </div>

      <div className={cn("p-5 transition-opacity duration-200", step === "questions" ? "opacity-100" : "opacity-0 pointer-events-none absolute inset-0")}>
        {currentQuestion ? (
          <>
            <div className="pr-8">
              <p className="text-[11px] text-muted-foreground">
                Quick follow-up (optional, {Math.min(questionIndex + 1, questions.length)} of {questions.length})
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Your score is already saved. Add context if you want.
              </p>
              <h3 className="mt-2 text-sm font-medium text-foreground">
                {currentQuestion.question}
              </h3>
            </div>

            <div className="mt-4 space-y-2.5">
              {currentQuestion.options.map((option) => {
                const checked = currentDraft.selectedOption === option
                return (
                  <label
                    key={option}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                      checked
                        ? "border-primary/30 bg-primary/5"
                        : "border-border/60 hover:border-border hover:bg-muted/40",
                    )}
                  >
                    <input
                      type="radio"
                      name={`${pageSlug}-${questionIndex}`}
                      checked={checked}
                      onChange={() => handleOptionSelect(option)}
                      className="mt-0.5 h-4 w-4 border-border text-primary focus:ring-primary"
                    />
                    <span className="flex-1 text-foreground">{option}</span>
                  </label>
                )
              })}
            </div>

            {selectedOther ? (
              <div className="mt-3 space-y-3">
                <Input
                  value={currentDraft.otherText}
                  onChange={(event) => handleOtherTextChange(event.target.value)}
                  placeholder="Add a little context"
                  className="h-9 border-0 border-b border-border rounded-none bg-transparent px-1 py-0 text-sm shadow-none focus-visible:ring-0"
                />
                {showContinueButton ? (
                  <Button type="button" size="sm" className="w-full" onClick={handleContinue}>
                    Continue
                  </Button>
                ) : null}
              </div>
            ) : null}

            {showSubmitButton ? (
              <Button
                type="button"
                size="sm"
                className="mt-3 w-full"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving…" : "Submit feedback"}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
